// web-staking-estimator-frontend/src/services/map/geometryService.js
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import Polygon from "@arcgis/core/geometry/Polygon";
import Extent from "@arcgis/core/geometry/Extent";

/**
 * Convert a JSON geometry object to an ESRI Geometry
 * @param {object} g - JSON geometry object
 * @param {object} sr - Spatial reference
 * @returns {Point|Polyline|Polygon|null} ESRI geometry or null
 */
export function toGeometry(g, sr) {
  if (!g) return null;
  
  if (typeof g.x === "number" && typeof g.y === "number") {
    return new Point({ x: g.x, y: g.y, spatialReference: sr });
  }
  
  if (Array.isArray(g.paths)) {
    return new Polyline({ paths: g.paths, spatialReference: sr });
  }
  
  if (Array.isArray(g.rings)) {
    return new Polygon({ rings: g.rings, spatialReference: sr });
  }
  
  return null;
}

/**
 * Get the combined extent of multiple graphics
 * @param {Array} graphics - Array of ESRI Graphics
 * @returns {Extent|null} Combined extent or null
 */
export function extentOf(graphics) {
  const geoms = graphics.map((g) => g.geometry).filter(Boolean);
  if (!geoms.length) return null;
  
  let ext = geoms[0].extent ?? null;
  for (let i = 1; i < geoms.length; i++) {
    const nextExt = geoms[i].extent ?? null;
    if (ext && nextExt) {
      ext = Extent.union(ext, nextExt);
    } else if (!ext && nextExt) {
      ext = nextExt;
    }
  }
  return ext;
}

/**
 * Clamp map scale to reasonable bounds
 * @param {number} scale - Desired scale
 * @returns {number} Clamped scale
 */
export function clampScale(scale) {
  const MIN = 600;
  const MAX = 20000;
  const s = Number(scale);
  if (!isFinite(s)) return 3000;
  return Math.max(MIN, Math.min(MAX, s));
}
